import { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { ChevronLeft, ChevronRight, X, ExternalLink, Play } from 'lucide-react';
import DOMPurify from 'dompurify';

const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

/**
 * B2B Promo Banner Display Component
 * Displays promotional banners for B2B client portal users
 * This is completely separate from the main app's PromoBannerCarousel
 */
const B2BPromoBannerDisplay = ({ theme }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState([]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-promos/active`, {
        withCredentials: true
      });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch B2B promo campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Auto-advance carousel
  useEffect(() => {
    if (campaigns.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % campaigns.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [campaigns.length]);

  const handleDismiss = async (campaignId) => {
    setDismissed(prev => [...prev, campaignId]);
    
    // Track dismissal
    try {
      await axios.post(`${API_BASE}/api/client-promos/${campaignId}/dismiss`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Silently fail - dismissal is tracked locally anyway
      console.debug('Failed to track dismissal:', error.message);
    }
  };

  const handleClick = async (campaign) => {
    // Track click
    try {
      await axios.post(`${API_BASE}/api/client-promos/${campaign.id}/click`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Silently fail - click tracking is non-critical
      console.debug('Failed to track click:', error.message);
    }

    // Handle CTA action
    if (campaign.cta_action === 'link' && campaign.cta_link) {
      window.open(campaign.cta_link, '_blank', 'noopener,noreferrer');
    }
  };

  // Filter out dismissed campaigns
  const visibleCampaigns = campaigns.filter(c => !dismissed.includes(c.id));

  if (loading || visibleCampaigns.length === 0) {
    return null;
  }

  const currentCampaign = visibleCampaigns[currentIndex % visibleCampaigns.length];
  if (!currentCampaign) return null;

  const renderVideoContent = (campaign) => {
    const videoUrl = campaign.video_url;
    
    if (!videoUrl) return null;

    // Check if it's an iframe embed code - sanitize to prevent XSS
    if (videoUrl.includes('<iframe')) {
      // Configure DOMPurify to allow iframes with specific attributes
      const sanitizedHtml = DOMPurify.sanitize(
        videoUrl.replace(/<iframe/g, '<iframe style="width:100%;height:100%;border:0;"'),
        { 
          ADD_TAGS: ['iframe'], 
          ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'style']
        }
      );
      return (
        <div 
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }} 
        />
      );
    }

    // YouTube URL
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.includes('youtu.be') 
        ? videoUrl.split('youtu.be/')[1]?.split('?')[0]
        : videoUrl.split('v=')[1]?.split('&')[0];
      
      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1`}
          className="w-full h-full"
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      );
    }

    // Vimeo URL
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0];
      return (
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1`}
          className="w-full h-full"
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture"
        />
      );
    }

    // Direct video file
    return (
      <video 
        src={videoUrl} 
        className="w-full h-full object-cover" 
        autoPlay 
        muted 
        loop 
        playsInline
      />
    );
  };

  return (
    <div className="relative w-full mb-6 rounded-xl overflow-hidden" style={{ minHeight: '200px' }}>
      {/* Banner Content */}
      <div
        className="relative w-full transition-all duration-500"
        style={{
          background: currentCampaign.banner_type === 'styled'
            ? `linear-gradient(135deg, ${currentCampaign.gradient_from || theme?.primary_color || '#df1283'}, ${currentCampaign.gradient_to || theme?.secondary_color || '#FF7901'})`
            : (theme?.background_secondary || '#1E293B'),
          minHeight: '200px'
        }}
      >
        {/* Image Banner */}
        {currentCampaign.banner_type === 'image' && currentCampaign.image_url && (
          <img 
            src={currentCampaign.image_url} 
            alt={currentCampaign.title}
            className="w-full h-full object-cover absolute inset-0"
            style={{ minHeight: '200px' }}
          />
        )}

        {/* Video Banner */}
        {currentCampaign.banner_type === 'video' && currentCampaign.video_url && (
          <div className="absolute inset-0">
            {renderVideoContent(currentCampaign)}
          </div>
        )}

        {/* Content Overlay */}
        <div 
          className="relative z-10 p-6 flex flex-col items-center justify-center text-center min-h-[200px]"
          style={{
            background: currentCampaign.banner_type !== 'styled' ? 'rgba(0,0,0,0.5)' : 'transparent'
          }}
        >
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            {currentCampaign.title}
          </h3>
          
          {currentCampaign.subtitle && (
            <p className="text-sm md:text-base text-white/90 mb-4 max-w-xl">
              {currentCampaign.subtitle}
            </p>
          )}

          {currentCampaign.cta_text && (
            <button
              onClick={() => handleClick(currentCampaign)}
              className="px-6 py-2 rounded-full font-medium transition-all hover:scale-105 flex items-center gap-2"
              style={{
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)'
              }}
            >
              {currentCampaign.cta_text}
              {currentCampaign.cta_action === 'link' && <ExternalLink className="w-4 h-4" />}
            </button>
          )}
        </div>

        {/* Dismiss Button */}
        {currentCampaign.allow_dismiss !== false && (
          <button
            onClick={() => handleDismiss(currentCampaign.id)}
            className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white/70 hover:text-white transition-all"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Navigation Arrows */}
      {visibleCampaigns.length > 1 && (
        <>
          <button
            onClick={() => setCurrentIndex(prev => (prev - 1 + visibleCampaigns.length) % visibleCampaigns.length)}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrentIndex(prev => (prev + 1) % visibleCampaigns.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dots Indicator */}
      {visibleCampaigns.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {visibleCampaigns.map((campaign, idx) => (
            <button
              key={campaign.id || `dot-${idx}`}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex % visibleCampaigns.length
                  ? 'bg-white w-4'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default B2BPromoBannerDisplay;
